import { useState, useCallback, useRef } from "react";
import { rollOne, updatePity, pickMysteryBox, getPrestigeBonus } from "../utils";
import { RARITY_ORDER, EPIC_RARITIES, COIN_VALUES, ROLL_COST } from "../constants";
import {
  playRollSound, playRaritySound,
  playCoinSound, playAchievementSound,
} from "../Sounds";
import { updateMissionProgress } from "../components/menus/Missions";

export default function useRoll({
  coins, boosts, inventory, pity, prestige,
  applyRollResult, spendCoins, addCoins, applyBoost,
  unlockAchievements, checkAchievements, buildAchState,
  rollCount, totalCoins, unlockedAch, cutscenes, soundEnabled, setBoosts,
}) {
  const [lastRoll,      setLastRoll]      = useState(null);
  const [lastIsDupe,    setLastIsDupe]    = useState(false);
  const [showEpic,      setShowEpic]      = useState(false);
  const [showSpin,      setShowSpin]      = useState(false);
  const [pendingRoll,   setPendingRoll]   = useState(null);
  const [pendingIsDupe, setPendingIsDupe] = useState(false);
  const [bulkResults,   setBulkResults]   = useState(null);
  const [rolling,       setRolling]       = useState(false);
  const [cooldown,      setCooldown]      = useState(false);
  const [newAchToast,   setNewAchToast]   = useState([]);
  const [missionToast,  setMissionToast]  = useState([]);
  const [toastMsgs,     setToastMsgs]     = useState([]);
  const autoTimerRef                      = useRef(null);

  // ── Process a single rolled card ──────────────────────────
  const processOne = useCallback((selected, currentInventory, currentCoins, currentRollCount, currentBoosts, currentPity) => {
    const { coinMult } = getPrestigeBonus(prestige);
    const isInInventory = currentInventory.some(i => i.name === selected.name);
    const baseValue     = COIN_VALUES[selected.rarity] || 0;
    let coinEarned      = isInInventory
      ? Math.round(baseValue * (currentBoosts.collection_insurance ? 3 : 1) * coinMult)
      : Math.round(baseValue * 2 * coinMult);
    if (currentBoosts.coin_multiplier > 0) coinEarned *= 2;

    const newPity = updatePity(currentPity, selected.rarity);
    const newBoosts = { ...currentBoosts };
    if (newBoosts.hot_streak > 0)       newBoosts.hot_streak--;
    if (newBoosts.coin_multiplier > 0)  newBoosts.coin_multiplier--;
    if (newBoosts.lucky_charm)          newBoosts.lucky_charm  = false;
    if (newBoosts.rare_surge)           newBoosts.rare_surge   = false;
    if (newBoosts.epic_guarantee)       newBoosts.epic_guarantee = false;
    if (newBoosts.jackpot)              newBoosts.jackpot      = false;
    if (newBoosts.hamster_magnet > 0)   newBoosts.hamster_magnet--;

    return { coinEarned, isDupe: isInInventory, newPity, newBoosts };
  }, [prestige]);

  // ── Single roll ────────────────────────────────────────────
  const doRoll = useCallback((wheelEnabled) => {
    if (coins < ROLL_COST || rolling || cooldown) return;
    if (soundEnabled) playRollSound();
    setRolling(true);
    spendCoins(ROLL_COST);

    const selected = rollOne(boosts, inventory, pity, prestige);
    if (!selected) { setRolling(false); return; }

    const { coinEarned, isDupe, newPity, newBoosts } = processOne(selected, inventory, coins, rollCount, boosts, pity);

    applyRollResult(selected, coinEarned, isDupe, newPity);
    setBoosts(newBoosts);

    // Mission tracking
    const mdata = updateMissionProgress("rolls", 1);
    if (!isDupe) updateMissionProgress("newHamsters", 1);
    updateMissionProgress("coinsEarned", coinEarned);
    if (RARITY_ORDER.indexOf(selected.rarity) > RARITY_ORDER.indexOf(mdata.progress.highestRarity || "Common")) {
      updateMissionProgress("highestRarity_set", selected.rarity);
    }

    if (soundEnabled) playRaritySound(selected.rarity);
    if (soundEnabled && coinEarned > 0) playCoinSound?.();

    setLastRoll(selected);
    setLastIsDupe(isDupe);

    // Achievement check
    const newInv     = isDupe ? inventory : [...inventory, selected];
    const newRollCnt = rollCount + 1;
    const newTotalC  = totalCoins + coinEarned;
    const achState   = buildAchState(newInv, newRollCnt, newTotalC, prestige, unlockedAch);
    const newAch     = checkAchievements(achState);
    if (newAch.length) {
      unlockAchievements(newAch.map(a => a.id));
      setNewAchToast(newAch.map(a => `🏆 ${a.name}`));
      if (soundEnabled) playAchievementSound?.();
    }

    // Epic cutscene
    const showCutscene = cutscenes?.[selected.rarity] !== false;
    if (EPIC_RARITIES.includes(selected.rarity) && showCutscene && !wheelEnabled) {
      setShowEpic(true);
    } else if (wheelEnabled) {
      setPendingIsDupe(isDupe);
      setPendingRoll(selected);
      setShowSpin(true);
    }

    setRolling(false);

    // Short cooldown prevents button spam
    setCooldown(true);
    setTimeout(() => setCooldown(false), 800);
  }, [coins, rolling, cooldown, soundEnabled, boosts, inventory, pity, prestige, rollCount, totalCoins, unlockedAch, cutscenes,
    spendCoins, applyRollResult, setBoosts, processOne, checkAchievements, buildAchState, unlockAchievements]);

  // ── Bulk roll (10x) ────────────────────────────────────────
  const doBulkRoll = useCallback((count = 10) => {
    if (coins < ROLL_COST * count || rolling) return;
    spendCoins(ROLL_COST * count);
    setRolling(true);

    let currentInv    = [...inventory];
    let currentPity   = { ...pity };
    let currentBoosts = { ...boosts };
    const results     = [];

    for (let i = 0; i < count; i++) {
      const selected = rollOne(currentBoosts, currentInv, currentPity, prestige);
      if (!selected) continue;
      const { coinEarned, isDupe, newPity, newBoosts } = processOne(selected, currentInv, coins, rollCount, currentBoosts, currentPity);
      results.push({ ...selected, earned: coinEarned, isDupe });
      if (!isDupe) currentInv = [...currentInv, selected];
      currentPity   = newPity;
      currentBoosts = newBoosts;
      updateMissionProgress("rolls", 1);
      if (!isDupe) updateMissionProgress("newHamsters", 1);
      updateMissionProgress("coinsEarned", coinEarned);
    }

    setBulkResults(results);
    results.forEach(r => applyRollResult(r, r.earned, r.isDupe, currentPity));
    setBoosts(currentBoosts);

    const totalEarned = results.reduce((s, r) => s + r.earned, 0);

    // Achievement check
    const finalInv   = [...inventory, ...results.filter(r => !r.isDupe)];
    const achState   = buildAchState(finalInv, rollCount + count, totalCoins + totalEarned, prestige, unlockedAch);
    const newAch     = checkAchievements(achState);
    if (newAch.length) {
      unlockAchievements(newAch.map(a => a.id));
      if (soundEnabled) playAchievementSound?.();
    }

    setRolling(false);
  }, [coins, rolling, inventory, pity, boosts, prestige, rollCount, totalCoins, unlockedAch,
    spendCoins, applyRollResult, setBoosts, processOne, checkAchievements, buildAchState, unlockAchievements, soundEnabled]);

  // ── Auto roll ──────────────────────────────────────────────
  const doAutoRoll = useCallback((count = 10) => {
    if (coins < ROLL_COST * count || rolling) return;
    let remaining = count;
    const tick = () => {
      if (remaining <= 0) { clearInterval(autoTimerRef.current); return; }
      doRoll(false);
      remaining--;
    };
    autoTimerRef.current = setInterval(tick, 500);
    tick();
  }, [coins, rolling, doRoll]);

  // ── Mystery box ────────────────────────────────────────────
  const doMysteryBox = useCallback(() => {
    const outcome = pickMysteryBox();
    if (outcome.type === "coins") {
      addCoins(outcome.value);
      setToastMsgs([outcome.label]);
    } else if (outcome.type === "boost") {
      applyBoost(outcome.value);
      setToastMsgs([outcome.label]);
    }
    return outcome;
  }, [addCoins, applyBoost]);

  return {
    lastRoll, lastIsDupe,
    showEpic, setShowEpic, showSpin, setShowSpin,
    pendingRoll, setPendingRoll,
    pendingIsDupe, setPendingIsDupe,
    bulkResults, setBulkResults,
    rolling, cooldown,
    newAchToast, setNewAchToast, missionToast, setMissionToast,
    toastMsgs, setToastMsgs, doRoll, doBulkRoll, doAutoRoll, doMysteryBox,
  };
}